import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  INVITATION_CONTINUATION_STATE,
  useAuth,
  type AuthFormError,
} from '@monecolier/ui';
import { invitationContinuation } from '../lib/invitationContinuation';

// Redirection après succès : "/" (Home) — pas de page protégée dédiée pour l'instant,
// voir AUT-01, extension frontend.
const REDIRECT_AFTER_LOGIN = '/';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // AUT-02 : message de confirmation transmis par ResetPassword.tsx via `state`
  // (jamais un paramètre d'URL — voir le commentaire équivalent dans
  // ResetPassword.tsx) après une réinitialisation de mot de passe réussie.
  const location = useLocation();
  const passwordResetConfirmation = (location.state as { passwordResetConfirmation?: string } | null)
    ?.passwordResetConfirmation;
  // AUT-33 : présent UNIQUEMENT quand on arrive depuis un lien d'invitation —
  // absent, cette page reste strictement inchangée.
  const [searchParams] = useSearchParams();
  const continuation = invitationContinuation.read(searchParams);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AuthFormError | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      // AUT-33 : connexion depuis un lien d'invitation — retour direct sur la
      // page d'acceptation (voir Register.tsx).
      if (continuation) {
        navigate(invitationContinuation.acceptPath(continuation), { replace: true, state: INVITATION_CONTINUATION_STATE });
        return;
      }
      navigate(REDIRECT_AFTER_LOGIN);
    } catch (err) {
      // describeAuthFormError() journalise TOUJOURS l'erreur réelle via
      // console.error avant de la traduire — voir packages/ui/src/auth/
      // authFormError.ts pour le bug réel (CORS silencieux) que cette
      // journalisation permanente permet désormais de diagnostiquer.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title={t('login.title')}>
      {passwordResetConfirmation ? (
        <p role="status" data-testid="login-password-reset-confirmation" className="text-sm text-brand-700">
          {passwordResetConfirmation}
        </p>
      ) : null}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          {t('login.email')}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          {t('login.password')}
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
            className={formInputClassName}
          />
        </label>
        {error ? (
          <p role="alert" data-testid="login-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
      <p className={secondaryTextClassName}>
        <Link to="/forgot-password" className={linkClassName}>
          {t('login.forgotPassword')}
        </Link>
      </p>
      <p className={secondaryTextClassName}>
        {t('login.noAccount')} <Link to={invitationContinuation.authPath('/register', continuation)} className={linkClassName}>{t('login.createAccount')}</Link>
      </p>
    </Card>
  );
}
